import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Receipt, DollarSign, Send, Plus, CheckCircle2, AlertCircle, Banknote, Trash2, Eye, Mail, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoicingPanel({ orgId, userRole }) {
  const { isDark } = useTheme();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showPayment, setShowPayment] = useState(null);
  const [detailData, setDetailData] = useState(null);

  // Create form
  const [form, setForm] = useState({ title: '', description: '', due_date: '', notes: '', line_items: [{ description: '', amount: '' }], recipient_type: 'all', recipient_team_id: '', recipient_ids: [], season: '', installments: [] });
  // Payment form
  const [payForm, setPayForm] = useState({ payer_name: '', amount: '', method: 'cash', reference: '', note: '' });
  const [families, setFamilies] = useState([]);
  const [famQ, setFamQ] = useState('');
  const [teams, setTeams] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);

  const canManage = ['ad', 'scheduler', 'company_support'].includes(userRole);   // Scheduler = AD here (AJ 2026-10-18)

  useEffect(() => { if (orgId) fetchData(); }, [orgId]);

  const fetchData = async () => {
    try {
      const [invRes, sumRes] = await Promise.all([
        axios.get(`${API_URL}/api/invoicing/${orgId}/invoices`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/${orgId}/summary`, { withCredentials: true }),
        axios.post(`${API_URL}/api/invoicing/${orgId}/check-overdue`, {}, { withCredentials: true }).catch(() => {}),
      ]);
      setInvoices(invRes.data || []);
      setSummary(sumRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchFamilies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoicing/${orgId}/families`, { withCredentials: true });
      setFamilies(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/data-provider/${orgId}/teams`, { withCredentials: true });
      setTeams(res.data?.teams || res.data || []);
    } catch (err) { setTeams([]); }
  };

  // I2 — split the total into N installments (≤24), a month apart from the first date; rows stay editable.
  const formTotal = form.line_items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const splitInstallments = (n, first) => {
    n = Math.max(0, Math.min(24, parseInt(n, 10) || 0));
    if (!n || !first) { setForm(f => ({ ...f, installments: [] })); return; }
    const per = Math.floor((formTotal / n) * 100) / 100;
    const rows = Array.from({ length: n }).map((_, i) => {
      const d = new Date(`${first}T12:00:00`); d.setMonth(d.getMonth() + i);
      return { due_date: d.toISOString().slice(0, 10), amount: i < n - 1 ? per : Math.round((formTotal - per * (n - 1)) * 100) / 100 };
    });
    setForm(f => ({ ...f, installments: rows }));
  };
  const familySeasons = [...new Set((families || []).map(f => f.season).filter(Boolean))].sort().reverse();

  const createInvoice = async () => {
    try {
      const items = form.line_items.filter(i => i.description && i.amount).map(i => ({ ...i, amount: parseFloat(i.amount) }));
      if (!form.batch_id) form.batch_id = (window.crypto?.randomUUID?.() || String(Date.now()));
      const res = await axios.post(`${API_URL}/api/invoicing/${orgId}/invoices`, {
        ...form, line_items: items, installments: (form.installments || []).map(r => ({ ...r, amount: parseFloat(r.amount) })),
      }, { withCredentials: true });
      alert(res.data.already_created ? `These ${res.data.count} invoice(s) were already created.` : `Created ${res.data.count} invoice(s); emailing ${res.data.emailed} contact(s).`);
      setShowCreate(false);
      setForm({ title: '', description: '', due_date: '', notes: '', line_items: [{ description: '', amount: '' }], recipient_type: 'all', recipient_team_id: '', recipient_ids: [], season: '', installments: [] });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Could not create invoice'); }
  };

  const sendReminder = async (invoiceId) => {
    setSendingReminder(invoiceId);
    try {
      const res = await axios.post(`${API_URL}/api/invoicing/invoice/${invoiceId}/send-reminder`, {}, { withCredentials: true });
      alert(`Reminders sent to ${res.data.reminders_sent} recipients`);
    } catch (err) { console.error(err); alert('Failed to send reminders'); }
    finally { setSendingReminder(null); }
  };

  const viewDetail = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoicing/invoice/${invoiceId}`, { withCredentials: true });
      setDetailData(res.data);
      setShowDetail(invoiceId);
    } catch (err) { console.error(err); }
  };

  const recordPayment = async () => {
    try {
      await axios.post(`${API_URL}/api/invoicing/invoice/${showPayment}/record-payment`, {
        ...payForm, amount: parseFloat(payForm.amount)
      }, { withCredentials: true });
      setShowPayment(null);
      setPayForm({ payer_name: '', amount: '', method: 'cash', reference: '', note: '' });
      fetchData();
      if (showDetail) viewDetail(showDetail);
    } catch (err) { console.error(err); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API_URL}/api/invoicing/invoice/${id}`, { withCredentials: true });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const addLineItem = () => setForm({ ...form, line_items: [...form.line_items, { description: '', amount: '' }] });
  const updateLineItem = (idx, field, val) => {
    const items = [...form.line_items];
    items[idx][field] = val;
    setForm({ ...form, line_items: items });
  };
  const removeLineItem = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/10 text-yellow-600', partial: 'bg-blue-500/10 text-blue-600',
      paid: 'bg-green-500/10 text-green-600', overdue: 'bg-red-500/10 text-red-600'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="invoicing-panel">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Invoices', value: summary.total_invoices, icon: Receipt, color: 'text-blue-500' },
            { label: 'Billed', value: `$${summary.total_billed?.toFixed(2)}`, icon: DollarSign, color: 'text-amber-500' },
            { label: 'Collected', value: `$${summary.total_collected?.toFixed(2)}`, icon: CheckCircle2, color: 'text-green-500' },
            { label: 'Outstanding', value: `$${summary.total_outstanding?.toFixed(2)}`, icon: AlertCircle, color: 'text-red-500' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
                    <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{value}</p>
                  </div>
                  <Icon className={`h-6 w-6 opacity-50 ${color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <RegInvoices orgId={orgId} isDark={isDark} />


      {/* Invoice List */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Receipt className="h-5 w-5 text-amber-500" /> Invoices
            </CardTitle>
            {canManage && (
              <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700"
                onClick={() => setShowCreate(true)} data-testid="invoice-create-btn">
                <Plus className="h-4 w-4 mr-1" /> New Invoice
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading...</p> :
          invoices.length > 0 ? (
            <div className="space-y-2">
              {invoices.map(inv => (
                <div key={inv.id} className={`flex items-center justify-between p-3 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800/30' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex-1 cursor-pointer" onClick={() => viewDetail(inv.id)}>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{inv.title}{inv.family_name ? <span className={`ml-2 text-xs font-normal ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{inv.family_name} · {inv.invoice_number}</span> : null}</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Due: {inv.due_date || 'N/A'} | Paid: ${inv.amount_paid?.toFixed(2)} / ${inv.total?.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(inv.status)}
                    {canManage && inv.status !== 'paid' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => sendReminder(inv.id)}
                          disabled={sendingReminder === inv.id}
                          className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid={`send-reminder-${inv.id}`}>
                          {sendingReminder === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setShowPayment(inv.id); setPayForm({...payForm, amount: inv.amount_due?.toFixed(2) || ''}); }}
                          className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid={`record-payment-${inv.id}`}>
                          <Banknote className="h-3 w-3 mr-1" /> Pay
                        </Button>
                      </>
                    )}
                    {canManage && (
                      <Button size="sm" variant="ghost" onClick={() => deleteInvoice(inv.id)} className="text-red-400">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Receipt className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No Invoices</p>
              <p className="text-sm mt-1">Create invoices for fees outside of registration</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CREATE INVOICE DIALOG */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}>
          <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Title</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Spring Season Fee"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="invoice-title" />
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="invoice-due-date" />
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Line Items</Label>
              {form.line_items.map((item, idx) => (
                <div key={idx} className="flex gap-2 mt-1">
                  <Input value={item.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} placeholder="Description"
                    className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} />
                  <Input type="number" value={item.amount} onChange={e => updateLineItem(idx, 'amount', e.target.value)} placeholder="$0.00" step="0.01"
                    className={`w-24 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} />
                  {form.line_items.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeLineItem(idx)} className="text-red-400"><Trash2 className="h-3 w-3" /></Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addLineItem} className={`mt-2 ${isDark ? 'border-slate-600 text-slate-300' : ''}`}>
                <Plus className="h-3 w-3 mr-1" /> Add Line
              </Button>
              <p className={`text-sm font-medium mt-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Total: ${form.line_items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0).toFixed(2)}
              </p>
            </div>
            <div data-testid="invoice-installments">
              <Label className={isDark ? 'text-slate-300' : ''}>Payment schedule (optional)</Label>
              <div className="flex gap-2 mt-1">
                <Input type="number" min="0" max="24" placeholder="# installments" className={`w-32 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                  onChange={e => splitInstallments(e.target.value, form.installments?.[0]?.due_date || form.due_date)} data-testid="invoice-inst-count" />
                <span className={`text-xs self-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>first due = Due Date above, then monthly (max 24)</span>
              </div>
              {(form.installments || []).map((r, i) => (
                <div key={i} className="flex gap-2 mt-1">
                  <Input type="date" value={r.due_date} className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                    onChange={e => setForm(f => { const a = [...f.installments]; a[i] = { ...a[i], due_date: e.target.value }; return { ...f, installments: a }; })} />
                  <Input type="number" step="0.01" value={r.amount} className={`w-28 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                    onChange={e => setForm(f => { const a = [...f.installments]; a[i] = { ...a[i], amount: e.target.value }; return { ...f, installments: a }; })} />
                </div>
              ))}
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Notes</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Recipients</Label>
              <select value={form.recipient_type} onChange={e => { setForm({...form, recipient_type: e.target.value, recipient_ids: [], recipient_team_id: ''}); if (e.target.value === 'individual') fetchFamilies(); if (e.target.value === 'team') fetchTeams(); }}
                className={`w-full rounded px-3 py-2 border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`} data-testid="invoice-recipient-type">
                <option value="all">All Families (one season)</option>
                <option value="team">Specific Team</option>
                <option value="individual">Specific Families</option>
              </select>
              {form.recipient_type === 'all' && (
                <select value={form.season} onChange={e => setForm({ ...form, season: e.target.value })} onFocus={() => { if (!families.length) fetchFamilies(); }}
                  className={`w-full rounded px-3 py-2 border text-sm mt-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`} data-testid="invoice-season">
                  <option value="">Latest season</option>
                  {familySeasons.map(se => <option key={se} value={se}>{se}</option>)}
                </select>
              )}
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Each family gets its own invoice, emailed to every primary contact.</p>
              {form.recipient_type === 'team' && (
                <select value={form.recipient_team_id} onChange={e => setForm({...form, recipient_team_id: e.target.value})}
                  className={`w-full rounded px-3 py-2 border text-sm mt-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}>
                  <option value="">Select Team</option>
                  {(teams || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
              {form.recipient_type === 'individual' && families.length > 0 && (
                <input className={`w-full rounded px-2 py-1 border text-sm mt-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`} placeholder="Search families…"
                  value={famQ} onChange={e => setFamQ(e.target.value)} data-testid="invoice-family-search" />
              )}
              {form.recipient_type === 'individual' && families.length > 0 && (
                <div className={`max-h-40 overflow-y-auto border rounded p-2 mt-2 space-y-1 ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                  <div className="flex justify-between text-xs mb-1"><span>{form.recipient_ids.length} selected</span>
                    <button className="underline" onClick={() => setForm({ ...form, recipient_ids: [...new Set([...form.recipient_ids, ...families.filter(f => !famQ || `${f.name} ${f.email} ${f.season}`.toLowerCase().includes(famQ.toLowerCase())).map(f => f.id)])] })}>Select all shown</button></div>
                  {families.filter(f => !famQ || `${f.name} ${f.email} ${f.season}`.toLowerCase().includes(famQ.toLowerCase())).map(f => (
                    <label key={f.id} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="checkbox" checked={form.recipient_ids.includes(f.id)}
                        onChange={e => setForm({...form, recipient_ids: e.target.checked ? [...form.recipient_ids, f.id] : form.recipient_ids.filter(id => id !== f.id) })} />
                      <span className={isDark ? 'text-slate-300' : ''}>{f.name}</span><span className="text-xs text-slate-400">{f.season}</span>
                      {f.primary_email && <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>({f.primary_email})</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={createInvoice} className="w-full bg-amber-600 text-white hover:bg-amber-700" data-testid="submit-invoice">
              <Send className="h-4 w-4 mr-1" /> Send Invoice
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* INVOICE DETAIL DIALOG */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}>
          <DialogHeader><DialogTitle>{detailData?.title}</DialogTitle></DialogHeader>
          {detailData && (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Status:</span>
                {statusBadge(detailData.status)}
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Due:</span>
                <span>{detailData.due_date || 'N/A'}</span>
              </div>
              <div>
                <p className={`text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : ''}`}>Line Items:</p>
                {detailData.line_items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span>{item.description}</span>
                    <span className="font-medium">${parseFloat(item.amount).toFixed(2)}</span>
                  </div>
                ))}
                <div className={`flex justify-between font-bold mt-2 pt-2 border-t ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                  <span>Total</span><span>${detailData.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-green-500 text-sm mt-1">
                  <span>Paid</span><span>${detailData.amount_paid?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-red-500 text-sm">
                  <span>Remaining</span><span>${detailData.amount_due?.toFixed(2)}</span>
                </div>
              </div>
              {detailData.payments?.length > 0 && (
                <div>
                  <p className={`text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : ''}`}>Payment History:</p>
                  {detailData.payments.map((p, i) => (
                    <div key={i} className={`flex justify-between text-sm p-1.5 rounded ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                      <span>{p.payer_name} ({p.method})</span>
                      <span className="text-green-500">${p.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RECORD PAYMENT DIALOG */}
      <Dialog open={!!showPayment} onOpenChange={() => setShowPayment(null)}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Payer Name</Label>
              <Input value={payForm.payer_name} onChange={e => setPayForm({...payForm, payer_name: e.target.value})}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="payment-payer" />
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Amount</Label>
              <Input type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: e.target.value})}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="payment-amount" />
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Method</Label>
              <select value={payForm.method} onChange={e => setPayForm({...payForm, method: e.target.value})}
                className={`w-full rounded px-3 py-2 border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Reference / Check #</Label>
              <Input value={payForm.reference} onChange={e => setPayForm({...payForm, reference: e.target.value})}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
            </div>
            <Button onClick={recordPayment} className="w-full bg-green-600 text-white hover:bg-green-700" data-testid="submit-payment">
              <Banknote className="h-4 w-4 mr-1" /> Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Registration-DERIVED billing (single source = reg_submissions): outstanding
// fees per registration. Real invoices (invoices collection) render below when
// they exist.
function RegInvoices({ orgId, isDark }) {
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState({ fees: 0, paid: 0, outstanding: 0, registrations: 0 });
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const m = (n) => `$${Number(n || 0).toFixed(2)}`;
  useEffect(() => {
    axios.get(`${API_URL}/api/financials/${orgId}/registrations`, { withCredentials: true })
      .then((r) => { setGroups(r.data.groups || []); setTotals(r.data.totals || {}); })
      .catch(() => setGroups([]));
  }, [orgId]);
  return (
    <Card className={card} data-testid="invoicing-registrations">
      <CardHeader><CardTitle className={`flex items-center gap-2 text-base ${text}`}>
        <Receipt className="h-4 w-4 text-amber-500" /> Registration billing (live)
        <span className={`text-xs font-normal ${sub}`}>{totals.registrations} reg · billed {m(totals.fees)} · paid {m(totals.paid)} · outstanding {m(totals.outstanding)}</span>
      </CardTitle></CardHeader>
      <CardContent>
        {groups.length === 0 ? <p className={`text-sm ${sub}`}>No registrations yet.</p> : (
          <div className="space-y-1 text-xs">
            {groups.map((g) => (
              <div key={g.form_id} className={`flex items-center gap-3 py-1 border-b border-slate-100 dark:border-slate-700/40 ${text}`} data-testid={`inv-reg-form-${g.form_id}`}>
                <span className="font-medium">{g.form_name}</span>
                <span className={sub}>{g.rows.length} registrations</span>
                <span className="ml-auto">billed {m(g.fees)} · paid {m(g.paid)} · <span className="text-red-500">outstanding {m(g.outstanding)}</span></span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
